/**
 * Base-view shared contract.
 *
 * This file is the SEAM between the pure-TS simulation (`sim/`, owned by the
 * logic agent + tested by qa) and the Three.js renderer/UI (`render/`, `ui/`,
 * owned by the frontend agent). Neither side imports the other — both depend
 * only on the interfaces below. Keep this file free of Three.js and DOM types.
 */

/** Ground-plane coordinate. World is XZ; Y is up (height). */
export interface Vec2 {
  x: number;
  z: number;
}

export type ObjectId = string;
export type NpcId = string;

/**
 * 'npc' marks a WorldObject as an NPC's own placed body — `modelUrl` is the
 * body GLB, `npcId` is that same NPC's id (never a different one, unlike
 * `stall`/`building`, which can be RUN BY an NPC without BEING one). Always
 * pair it with `noClip: true` (a standing character shouldn't block the
 * player) — see ultra_map_editor's `spawnNpc()`, the one place that creates
 * these.
 *
 * 'grass' is a procedural decal-like prop with no `modelUrl` at all — see
 * `render/grass-mesh.ts`. `size.w`/`.h`/`.d` scale it like any other object;
 * `rotationY` is meaningless for it (it billboards to face the camera every
 * frame — see `SceneHandles.updateGrass`). Always pair with `noClip: true`.
 */
export type WorldObjectKind = 'stall' | 'prop' | 'building' | 'gate' | 'loot' | 'npc' | 'grass';

/**
 * What a `kind: 'npc'` body is visibly doing while idle. Lives here rather
 * than beside the animator that consumes it, because this file is the
 * Three.js-free seam and map data (`data/maps/*.ts`) has to be able to author
 * the value without pulling the renderer in. `render/character-animations.ts`
 * maps each of these to a legs+arms clip pair.
 */
/**
 * `death` is terminal, not a steady state like the rest — it plays once
 * (falls, buckles, lands) and holds the last frame forever, never looping
 * and never switched away from. See `NPC_ACTIVITY_CLIPS.death`'s comment in
 * character-animations.ts.
 *
 * `cheer` / `clap` / `crossed-arms` are the arena crowd's reactions
 * (`author_crowd_reactions.py`, 2026-09-19). They are ordinary steady
 * states like `stand` — the only one with a legs half of its own is
 * `cheer`, whose hop lives in `cheer-legs` because an arms clip may not
 * animate translation (`character-animations.channels.test.ts`).
 *
 * DECLARED AS A VALUE, not a bare union, because two consumers need to
 * ENUMERATE it: `NPC_ACTIVITY_CLIPS` (compiler-checked already, being a
 * `Record<NpcActivity, …>`) and the map editor's activity dropdown, which
 * was NOT — `ultra_map_editor/main.ts` hand-kept `readonly NpcActivity[]`
 * listing four of the seven that existed, so `walk`, `sleep` and `death`
 * were unauthorable in the editor while staying perfectly type-correct.
 * Deriving the type from the array makes that drift impossible.
 */
export const NPC_ACTIVITIES = [
  'stand', 'sit', 'work', 'aim', 'walk', 'sleep', 'death',
  'cheer', 'clap', 'crossed-arms',
] as const;

export type NpcActivity = typeof NPC_ACTIVITIES[number];

/**
 * One stop on a scripted NPC routine: go somewhere, do something, wait, move
 * on. The list cycles forever.
 *
 * This exists because `wanderRadius` can only express "drift about aimlessly".
 * A settlement reads as inhabited when people are visibly running errands —
 * fetching food, working a shift, reporting to the overseer — and that needs
 * ordered stops with durations and per-stop activities, not a random walk.
 */
export interface RoutineStep {
  /**
   * Where to stand for this step. Omit to stay put, which is how a step that
   * only changes activity (stand -> work at the same spot) is expressed.
   */
  position?: Vec2;
  /** What to do once here. Defaults to `'stand'`. */
  activity?: NpcActivity;
  /**
   * Seconds to remain before advancing. The travel time to get here is NOT
   * included — this is dwell only, so a step's cost is predictable no matter
   * how far the previous stop was.
   */
  dwell: number;
  /**
   * Yaw to face while dwelling (radians, 0 = +Z). Omit to keep whatever
   * heading the NPC arrived on. Set it for anything that must face something
   * specific — a shooter facing downrange, two people facing each other.
   */
  facing?: number;
  /**
   * Hide the body for this step's dwell — "goes inside to report and comes
   * back out". A hidden NPC is also withdrawn as an interaction target, so
   * the player cannot talk to someone who is visibly not there.
   */
  hidden?: boolean;
}

/** An ordered, looping set of stops — see `RoutineStep`. */
export interface NpcRoutine {
  /** At least one step. Cycles back to the first after the last. */
  steps: RoutineStep[];
  /**
   * Travel speed, m/s. Defaults to the ambient citizen stroll (1.6). A
   * patrolling guard may want more; nobody should approach the player's 3.5,
   * which reads as running.
   */
  speed?: number;
}

/** A placeholder world object (rendered as a box/cylinder for now). */
export interface WorldObject {
  id: ObjectId;
  kind: WorldObjectKind;
  label: string;
  /** Footprint centre on the ground plane. */
  position: Vec2;
  /** Placeholder box dimensions (metres). */
  size: { w: number; d: number; h: number };
  /** Placeholder material colour (hex, e.g. 0x9a4a30). */
  color: number;
  /** Round footprint instead of a box (cylinder placeholder). */
  round?: boolean;
  /** Can the player talk to this object's owner? */
  interactable: boolean;
  /**
   * Owner/runner NPC — one of `npcId`/`lootObjectId` is required when
   * `interactable` (never both; see `validateWorld` in sim/base-sim.ts).
   */
  npcId?: NpcId;
  /**
   * A Location's searchable loot object (S17, direct PoI visit) — the
   * alternative to `npcId` for `interactable`. Opaque to the sim: it's
   * echoed back on `NearestInteractable`/`InteractionState` for the caller
   * to correlate against its own loot-object list and fire a search request.
   * Location worlds have no NPCs at all (rule 10 — "no ambient NPCs in
   * location maps"), so every interactable object there uses this instead.
   */
  lootObjectId?: string;
  /**
   * Optional real GLB model shown instead of the primitive kit — `size`
   * still governs collision/interaction-range footprint regardless, so it
   * must be kept in sync with the model's actual (scaled) footprint.
   */
  modelUrl?: string;
  /** Target bounding-box height (metres) the model is scaled to on load. */
  modelHeight?: number;
  /**
   * 1-6 — which `HOME_LEVEL_MODELS` entry (`render/model-catalog.ts`) this
   * is currently showing, for the player home/shack building family only.
   * Author-time metadata, kept in sync with `modelUrl` by
   * `ultra_map_editor`'s "Level" control (`setHomeLevel()`) — the render
   * pipeline never reads this field itself, only `modelUrl`. No
   * player-progression system drives this yet; it's a placed choice, not a
   * live game-state value.
   */
  homeLevel?: number;
  /**
   * Optional collision footprint overrides (metres). Used instead of `size`
   * for collision/proximity calculations in sim. Useful when the visual
   * model's footprint doesn't match its actual walkable area — e.g. a
   * building with decorative geometry that extends beyond the core
   * structure. Defaults to `size.w`/`size.d` if omitted.
   */
  collisionW?: number;
  collisionD?: number;
  /**
   * For `kind: 'npc'` bodies only — what this NPC is visibly doing while
   * idle (`'stand'` if omitted). Drives the ambient legs/arms clip pair in
   * `createNpcAnimator`. Only has a visible effect if `modelUrl` points at a
   * RIGGED model — an unrigged body stays static whatever this says, because
   * the animator no-ops rather than throwing (see `createNpcAnimator`'s doc
   * comment). Every shipped `char-*`/`npc-*` body carries the skeleton as of
   * 2026-09-09 and `character-models.test.ts` holds that from the shipped
   * bytes, so in practice this is only a trap for a newly-added model.
   */
  npcActivity?: NpcActivity;
  /**
   * For `kind: 'npc'` bodies only — this body's own name, shown on its floating
   * label (`render/name-tags.ts`).
   *
   * The escape hatch for a body with NO entry in `WorldData.npcs` — which is
   * sixteen of New Ashford's twenty-four, and will be every enemy. Those cannot
   * simply be given an `npcs` entry instead: `validateWorld` requires a local
   * dialog tree for every NPC in that record, so adding one to name a passer-by
   * would stop the settlement booting.
   *
   * Precedence: this wins over `npcs[npcId].name`, which wins over the generic
   * fallback. Deliberately NOT a fallback to `label` — `label` is author-facing
   * and on these bodies it describes an ACTIVITY ("Sleeping Off a Shift"),
   * which over someone's head reads as a bug.
   *
   * For an NPC that HAS an `npcs` entry, edit the name there instead: that one
   * is what the dialog panel and the "Press E to talk to …" prompt read, and
   * setting this as well would make the same person answer to two names.
   */
  displayName?: string;
  /**
   * For `kind: 'npc'` bodies only — is this body an ENEMY?
   *
   * Drives the colour of its floating name label (`render/name-tags.ts`):
   * blue for an ordinary NPC, red for a hostile (green is the player's own,
   * which no placed body can have). Omitted/false = ordinary, which is every
   * body in every map that ships today — nothing in the rendered
   * game is hostile yet (`location-view.ts` is loot-only by design and the
   * raid resolver has no coordinates), so this is the authoring half of a
   * feature whose other half has no subject to point at.
   *
   * Lives here rather than on `Npc` because a hostile body will have no entry
   * in `WorldData.npcs` at all — the same shape the ambient citizens already
   * have — so an `Npc`-side flag would be unreachable for exactly the things
   * it is for.
   *
   * Nothing else reads it: it is NOT a combat flag, and the sim treats a
   * hostile body like any other body.
   */
  hostile?: boolean;
  /**
   * For `kind: 'npc'` bodies only — a held prop/tool shown parented to
   * `hand.R`, keyed into `NPC_HELD_ITEM` (`render/character-animations.ts`).
   * Independent of `npcActivity`: a farmer holds `'rake'` for the visual
   * regardless of whether they're currently `'work'`ing or just `'stand'`ing
   * nearby, and the raking motion itself comes entirely from `npcActivity:
   * 'work'`'s `work-raking-arms` clip. An unknown key is ignored (nothing
   * held), matching `modelUrl`'s own load-failure tolerance.
   */
  heldItemKey?: string;
  /**
   * For `kind: 'npc'` bodies only — item keys this body is WEARING, as
   * opposed to `heldItemKey` which is what it is holding.
   *
   * Each key is resolved the same way the player's own equipment is: a key in
   * `GARMENT_KEYS` loads `/models/garments/<key>.glb` and is rebound to this
   * body's skeleton (so sleeves follow the arms); a key in
   * `TORSO_MODEL_BY_KEY` is rigidly attached to `chest`. An unknown key is
   * ignored, matching `heldItemKey`/`modelUrl`'s own tolerance.
   *
   * NOT routed through `gameStateStore` — that store holds the PLAYER's
   * equipment and is single-subject by construction. A placed body's outfit
   * is map data, like its held item is.
   */
  equippedKeys?: readonly string[];
  /**
   * For `kind: 'npc'` bodies only — metres this NPC strolls from its authored
   * `position`, marking it an ambient wandering citizen. The sim walks it to a
   * random point inside the radius, then picks another, forever; `position`
   * stays the authored anchor (it is never written back) so a bake can't make
   * a citizen drift across the map over successive sessions.
   *
   * Pair with `npcActivity: 'walk'` — the walk cycle plays continuously, so a
   * wanderer never stands still. Buildings are avoided by rejecting candidate
   * targets inside a solid footprint; `noClip` on the NPC only stops the
   * PLAYER being pushed out of it and does nothing for the NPC's own movement
   * (see `resolveCollisions`), so containment is this field's job, not the
   * collision system's.
   */
  wanderRadius?: number;
  /**
   * For `kind: 'npc'` bodies only — a scripted, looping routine (see
   * `NpcRoutine`). Mutually exclusive with `wanderRadius`: a body cannot both
   * follow a script and drift randomly, and `validateWorld` rejects both being
   * set rather than silently picking one.
   *
   * Like `wanderRadius`, the authored `position` stays the anchor and is never
   * written back, so a bake cannot make a routine NPC creep across the map
   * over successive sessions.
   */
  routine?: NpcRoutine;
  /** Yaw in radians (0 = facing +Z), matching `PlayerState.facing`. Collision follows this too (see base-sim.ts's resolveAabb) — a rotated object's footprint rotates with it. */
  rotationY?: number;
  /** Pitch (tilt forward/back) in radians, applied after yaw. Default 0 (no tilt). */
  rotationX?: number;
  /** Roll (tilt left/right) in radians, applied after yaw and pitch. Default 0 (level). */
  rotationZ?: number;
  /** Skip collision entirely for this object — purely decorative, walk-through (default false: collides, matching every object's behavior before this field existed). */
  noClip?: boolean;
  /**
   * Does this object break line of sight? (`vision.ts`'s `segmentHitsBox`.)
   *
   * ─── Deliberately TRI-state ─────────────────────────────────────────────
   * `true`/`false` are AUTHORED answers, set by the map editor's "blocks
   * vision" checkbox and never overridden. `undefined` means "nobody has
   * decided", and the bake then derives one from the object's own dimensions
   * (see `occludesByDefault` in `scripts/bake_arena_geometry.ts` — tall and
   * wide enough to hide a person behind).
   *
   * The tri-state is the whole point: a map with 86 barriers should not need
   * 86 checkbox clicks to behave sensibly, but a waist-high crate an author
   * decides you CAN see over must be able to say so and survive a re-bake.
   * Which is why the serializer emits this field even when `false` — see
   * `serializeObject`, where every other boolean is emitted only when truthy
   * and an authored `false` would come back as `undefined` (i.e. "derive it")
   * on the next bake.
   *
   * Unrelated to `noClip`: sight and movement are separate questions. A chain
   * fence stops you walking and not looking; a smoke bank is the reverse.
   */
  blocksVision?: boolean;
  /**
   * Vertical offset (metres) from the ground plane, defaults to 0. Purely
   * visual — corrects a GLB whose pivot isn't at its true visual base (model
   * floats above or sinks into the ground). Collision in base-sim.ts never
   * reads this, only `position`/`size.w`/`size.d` — an object's footprint
   * stays ground-anchored regardless of how its model is nudged vertically.
   */
  positionY?: number;
}

/**
 * A freestanding invisible collision volume — no visual representation, no
 * `WorldObject` semantics (no `kind`/`label`/`interactable`/`npcId`). For
 * custom invisible walls/barriers that aren't tied to any rendered object.
 * Supports rotation (yaw only) — base-sim.ts's `resolveBoxAt` does real
 * oriented-box collision (transforms the query point into the zone's local
 * frame), not just axis-aligned, so an angled zone actually collides at its
 * true angle.
 */
export interface CollisionZone {
  id: string;
  label: string;
  position: Vec2;
  size: { w: number; d: number };
  /** Yaw in radians (0 = facing +Z), matching `PlayerState.facing`. */
  rotationY?: number;
  /**
   * Does this zone break line of sight? Default **false** — an invisible
   * volume is a boundary rail far more often than it is a wall, and every
   * zone authored before this field existed is a rail.
   *
   * The opposite default to `WorldObject.blocksVision`, and deliberately so:
   * an object has a visible body whose size says what it should hide, a zone
   * has nothing to derive from. So this one is a plain boolean, not a
   * tri-state — there is no "derive it" answer to leave open.
   */
  blocksVision?: boolean;
  /**
   * Does this zone stop movement? Default **true** — that is what every
   * existing zone is for, and what the name says.
   *
   * Set `false` together with `blocksVision: true` for the sight-only case:
   * a smoke bank, a dust cloud, a dark room you cannot see into but can walk
   * straight through.
   */
  blocksMovement?: boolean;
  /**
   * Optional ground-fog visual over this zone's footprint — "fog is a zone"
   * rather than a separate top-level concept, so it inherits the zone's own
   * position/size/rotation for free. Omit for an ordinary invisible-in-play
   * collision volume (unchanged default). See `render/fog-zone.ts` for the
   * rendering approach.
   */
  fog?: {
    /** Fog tint (hex, e.g. 0x8a9a9a). */
    color: number;
    /** 0-1 base opacity of the densest (ground-level) layer. */
    opacity: number;
    /**
     * Visual height (metres) the fog stack rises to. Display-only, like
     * `debug-zones.ts`'s zone wireframe height — `resolveBoxAt` in
     * base-sim.ts is XZ-only and never reads this; it does not change what
     * the zone collides with.
     */
    height: number;
  };
}

/**
 * A flat, ground-projected texture patch — grass, puddles, dirt, torn paper,
 * shade. Purely decorative: no `WorldObject` semantics, no collision, always
 * lies on the ground plane (y=0). Rendered as an alpha-cutout quad — see
 * `render/decal-mesh.ts`.
 */
export interface Decal {
  id: string;
  label: string;
  /** Path under `public/decals/` (e.g. `/decals/puddle.webp`). */
  textureUrl: string;
  position: Vec2;
  size: { w: number; d: number };
  /** Yaw in radians (0 = facing +Z), matching `PlayerState.facing`. */
  rotationY?: number;
  /** 0-1, defaults to fully opaque (still alpha-cutout by the texture's own alpha channel regardless). */
  opacity?: number;
  /**
   * Alpha-cutout threshold (three's `Material.alphaTest`), 0-1, defaults to
   * 0.1 — matches the hardcoded value every decal rendered at before this
   * field existed. Lower = softer: more of the texture's semi-transparent
   * edge pixels survive the cutout and blend, which is what "ease" on a
   * shadow-blob decal actually means (its blur is baked into the texture;
   * this controls how much of that blur the cutout throws away). Higher =
   * harder: a crisper, more abrupt edge. 0 disables the cutout entirely
   * (every non-zero-alpha pixel blends).
   */
  hardness?: number;
}

/**
 * Road/path surface. Each maps to one tiling texture in
 * `render/road-mesh.ts`'s `ROAD_SURFACES` — the URL lives there, not here, so
 * this file stays free of asset paths the sim has no use for (same split as
 * `NpcActivity` -> clip names).
 */
export type RoadSurface = 'dirt_road' | 'dirt_path' | 'asphalt_old' | 'masonry_path';

/**
 * A procedurally-generated road or path: an author places a polyline of
 * points and the renderer builds a ribbon of geometry along it. This is the
 * "place points and a road appears between them" model from a city-builder,
 * rather than placing individual road-segment props.
 *
 * Purely decorative, like `Decal` — no collision, no `WorldObject` semantics,
 * always flat on the ground plane. A road that should block movement needs a
 * `CollisionZone` over it, deliberately: most roads are the one part of a
 * settlement you specifically CAN walk on.
 *
 * Why a polyline and not a spline: the ribbon is mitred at each interior
 * point (see `buildRoadMesh`), so a dense-enough point list already reads as
 * a curve, and a polyline is what a click-to-place editor produces natively.
 * Adding curvature later means subdividing `points` at author time, with no
 * change to this contract or to the renderer.
 */
export interface Road {
  id: string;
  label: string;
  surface: RoadSurface;
  /**
   * Centreline, in order. Needs at least 2 points; consecutive duplicates are
   * skipped by the mesh builder (a click-to-place UI produces them easily).
   */
  points: Vec2[];
  /** Full width across the ribbon, metres. */
  width: number;
  /**
   * Metres of road per texture repeat ALONG its length. Omitted = the
   * surface's own default (`ROAD_SURFACES[surface].tileLength`).
   *
   * This is the field that makes the masonry path read as laid pavers rather
   * than a smear: the texture's V axis is mapped to arc length, so the paving
   * pattern advances at a fixed metres-per-tile rate no matter how the road
   * bends, and never stretches through a corner.
   */
  tileLength?: number;
  /**
   * 0-1, defaults to fully opaque. Below 1 the road blends into the ground
   * texture underneath, which is what an old, half-reclaimed road wants.
   */
  opacity?: number;
}

/**
 * One end of a cable span: a point on the ground plus the height the cable
 * attaches at. Height is separate from `Vec2` because a cable's whole reason
 * for existing is that it hangs ABOVE the world — a pole-top anchor at 5.5 m
 * and a mast at 8 m are what give the run its shape.
 */
export interface CableAnchor {
  position: Vec2;
  /** Metres above the ground plane where the cable attaches. */
  height: number;
}

/**
 * A hanging power cable: an ordered run of anchors with a droop between each
 * consecutive pair.
 *
 * Same authoring model as `Road` — an ordered point list that the renderer
 * turns into generated geometry — which is why it is a top-level array rather
 * than a `WorldObject`. It has no footprint, no single position, and no
 * collision; it is decorative.
 *
 * `sag` is what stops it reading as a wireframe. A dead-straight line between
 * two pole tops looks like a debug gizmo; real cable hangs.
 */
export interface Cable {
  id: string;
  label: string;
  /** At least 2 anchors, in order. Each consecutive pair is one hanging span. */
  anchors: CableAnchor[];
  /**
   * Metres the cable droops below the straight chord at the middle of each
   * span. Scaled per span by its length, so a long span sags more than a
   * short one from the same value. Omitted = `DEFAULT_CABLE_SAG`.
   */
  sag?: number;
  /** Cable thickness in metres. Omitted = `DEFAULT_CABLE_RADIUS`. */
  radius?: number;
  /** Hex colour. Omitted = the renderer's weathered-cable default. */
  color?: number;
}

/**
 * A rotated rectangular region marking a raid-relevant area — where a raid
 * player can extract (`WorldData.escapeZones`) or where enemy raiders can
 * spawn (`WorldData.raiderSpawnZones`). Deliberately its own type rather than
 * reusing `CollisionZone`: `CollisionZone` is a REAL collider fed into
 * base-sim.ts's `resolveBoxAt` (see that type's own doc comment) — this is
 * not a collider at all, just a marked-off rectangle, and giving it the same
 * type would wrongly imply it blocks movement.
 *
 * Shape otherwise mirrors `CollisionZone`: rotated rectangle + an optional
 * visual sub-object (`marker`, the same "whole nested object, optional" shape
 * as `CollisionZone.fog`).
 *
 * SCOPE: this is map-editor authoring only — schema + serializer + round-trip.
 * Nothing in `backend/src/game/raid/raid.service.ts` (an abstract step
 * resolver with NO coordinate concept at all) reads these yet, and nothing in
 * `render/scene.ts`/`model-attach.ts` renders the optional `marker` in the
 * live game yet. Wiring either up is a separate, larger feature — see
 * checklist.md's "two raid maps are not reachable in play" known issue, which
 * this does not resolve.
 */
export interface RaidZone {
  id: string;
  label: string;
  position: Vec2;
  size: { w: number; d: number };
  /** Yaw in radians (0 = facing +Z), matching `CollisionZone.rotationY`. */
  rotationY?: number;
  /**
   * Optional visual marker for this zone. Omit for an invisible-in-play
   * region (the default — matches every zone before this field existed).
   * Not rendered anywhere yet (see this type's SCOPE note) — authored and
   * round-tripped only, for now.
   */
  marker?: {
    /** Tint (hex, e.g. 0x4dd6ff for an escape zone, 0xff5c3a for a raider spawn). */
    color: number;
    /** 0-1 base opacity. */
    opacity: number;
    /** Visual height (metres), same display-only convention as `CollisionZone.fog.height`. */
    height: number;
  };
}

export type SpriteSpawnKind = 'fire' | 'smoke';

/**
 * A standalone, hand-placed sprite-VFX billboard — fire/smoke authored
 * anywhere the author wants one, independent of any object.
 *
 * Since 2026-09-15 this is the ONLY source of fire and smoke in the game: the
 * model-keyed auto-attach that used to light every campfire and fire barrel
 * was removed (see `render/sprite-fx.ts`) because it drew a second flame the
 * map author could neither see in the editor nor tune. One entry here places
 * exactly one sprite, and nothing is implied by any model.
 *
 * Purely decorative, like `Decal` — no collision, no `WorldObject` semantics.
 * `width`/`height` name the billboard's own size, matching
 * `render/sprite-fx.ts`'s `SpriteOptions.width`/`.height` (NOT a ground
 * footprint like `WorldObject.size.w`/`.d` — a sprite is a camera-facing
 * card, it has no footprint).
 *
 * Rendered by `render/sprite-fx.ts`'s `buildSpriteSpawns`, built into the
 * scene by `render/scene.ts` and advanced each frame from `base-view/index.ts`
 * — so a spawn placed in `ultra_map_editor` burns in the live game too.
 */
/**
 * One authored fighter spawn in a PvP map.
 *
 * ─── Why this exists, when `spawnPoint` already did ─────────────────────────
 * `WorldData.spawnPoint` answers "where does the local player stand when this
 * map loads" — one point, client-side, a default the sim falls back to. A duel
 * needs two, they must be far apart and clear of cover, and the choice of who
 * stands where belongs to the server that built the match.
 *
 * Until this landed the server did not read the map at all: it hardcoded
 * `ARENA_SPAWN_Z = 24` (`backend/src/game/battleground/battleground.service.ts`)
 * and the two were kept in agreement by a COMMENT in `arena_pit.test.ts` plus
 * the map's own `spawnPoint.z` happening to match. Moving a spawn in the editor
 * changed nothing about where anyone actually stood.
 *
 * ─── How it reaches a server that cannot import this file ───────────────────
 * Maps are frontend TypeScript. `frontend/scripts/bake_arena_spawns.ts` reads
 * every `battleground`-category map in `MAP_REGISTRY` and writes the spawn
 * table into `backend/src/game/battleground/content/arena-spawns.ts`, which the
 * service imports. The editor stays the single authoring surface; the bake is
 * the crossing.
 */
export interface PlayerSpawn {
  id: string;
  /** Shown in the editor's object list. Never player-facing. */
  label: string;
  position: Vec2;
  /**
   * Which way the fighter faces at the bell, radians, 0 = +Z — the same yaw
   * convention as `WorldObject.rotationY` and `BaseSim.facing`.
   *
   * Omitted means "face the middle of the map", which the bake computes from
   * the position rather than storing: an author who moves a spawn should not
   * also have to remember to re-aim it.
   */
  rotationY?: number;
}

export interface SpriteSpawn {
  id: string;
  label: string;
  /** Which sheet — see `SPRITE_SHEETS` in `render/sprite-fx.ts`. */
  sheet: SpriteSpawnKind;
  position: Vec2;
  /**
   * Metres above the ground plane, same convention as `WorldObject.positionY`:
   * it offsets the BASE of the thing, not its centre. Defaults to 0, which
   * therefore sits the bottom edge of the card on the ground.
   *
   * Worth stating outright because a `THREE.Sprite` is positioned by its
   * CENTRE, so the renderer adds `height / 2` (`spawnCenterY`). Authoring it
   * as a centre instead would bury the lower half of every fire at the
   * default — and a flame whose base is hidden is the normal case, so the
   * mistake reads as "looks fine" until a tall smoke column is placed.
   */
  positionY?: number;
  /** Billboard width, metres. */
  width: number;
  /** Billboard height, metres. */
  height: number;
  /** 0-1 overall strength. Omitted = `SpriteOptions`'s own default (1). */
  opacity?: number;
  /** Multiplies into the sheet's colour (hex). Omitted = no tint (white). */
  color?: number;
}

/** The character that owns/runs an interactable object. */
export interface Npc {
  id: NpcId;
  name: string;
  role: string;
  /** Portrait asset URL (imported PNG) shown in the dialog UI. */
  portrait: string;
  /** Entry node id into this NPC's dialog tree. */
  dialogRootId: string;
  /**
   * Optional standing body shown in the world (reuses a character-select GLB
   * — placeholder, not bespoke NPC art). Omit to leave the NPC bodiless (only
   * their stall is visible), e.g. for an NPC with no assigned model yet.
   */
  bodyModelUrl?: string;
  /** World position for the body, independent of the stall's footprint. */
  bodyPosition?: Vec2;
  /** Yaw in radians (0 = facing +Z), matching `PlayerState.facing`. */
  bodyFacing?: number;
}

export interface DialogOption {
  label: string;
  /** Next node id, or null to end the conversation. */
  to: string | null;
  /** What the option does, for its icon. Server-derived; absent for plain talk. */
  kind?: 'trade' | 'mission' | 'accept';
  /** Shown but unchoosable; the text says what is missing. */
  locked?: string;
}

export interface DialogNode {
  id: string;
  npcId: NpcId;
  text: string;
  options: DialogOption[];
  /** Under the text on a quest brief: what the job needs and pays. */
  card?: { title: string; objectives: string[]; reward: string };
}

export type DialogTree = Record<string, DialogNode>;

/**
 * Ground rendering config — TWO tiling ground textures blended into each other
 * by a THIRD tiling texture used as a mask: where the mask is white the base
 * shows, where it is black the secondary shows. Because the two ground
 * textures repeat on their own grids and the mask repeats on a third, the
 * combination has no single visible repeat period — that is what kills the
 * tiling pattern the eye picks up on a lone repeated texture.
 *
 * Each of the three layers carries its own scale and rotation. Omitting the
 * whole field keeps the flat placeholder ground color. Setting only one of
 * `textureUrl`/`secondaryUrl` covers the ground with that one texture (the
 * mask has nothing to blend and is ignored); the blend needs all three.
 *
 * See `render/scene.ts` (the blend itself) and `render/ground-uv.ts` (why the
 * per-layer transform is built in metric space rather than via
 * `Texture.repeat`/`Texture.rotation`).
 */
export interface GroundTextureConfig {
  /** Base tiling texture under `public/textures/` — shown where the mask is WHITE. */
  textureUrl?: string;
  /** Tiles per world unit for the base texture — higher = smaller, more frequent tiles. */
  textureScale?: number;
  /** Rotation (radians) of the base texture's tile grid. */
  textureRotation?: number;
  /** Secondary tiling texture — shown where the mask is BLACK. */
  secondaryUrl?: string;
  /** Tiles per world unit for the secondary texture, independent of the base's. */
  secondaryScale?: number;
  /** Rotation (radians) of the secondary texture's tile grid. */
  secondaryRotation?: number;
  /** Noise texture used as the blend mask between base (white) and secondary (black). Sampled from its green channel, undecoded — author it greyscale. */
  noiseUrl?: string;
  /** Tiles per world unit for the mask — low values give large blotches, which is what breaks up the two ground textures. */
  noiseScale?: number;
  /** Rotation (radians) of the mask's tile grid. */
  noiseRotation?: number;
  /** Added to the mask value after gain, before clamping to [0, 1] — shifts the blend toward base (positive) or secondary (negative). */
  noiseOffset?: number;
  /** Multiplies the mask's contrast around its 0.5 midpoint before offset — above 1 sharpens the blotches toward pure base/secondary, below 1 flattens toward an even 50/50 mix. */
  noiseGain?: number;
}

/** Static world description consumed by both sim and renderer. */
export interface WorldData {
  cityName: string;
  ground: { width: number; depth: number };
  objects: WorldObject[];
  npcs: Record<NpcId, Npc>;
  dialog: Record<NpcId, DialogTree>;
  /** Player spawn on the ground plane. Falls back to the sim's own default (see base-sim.ts's SimOptions.spawn) when omitted. */
  spawnPoint?: Vec2;
  /** Freestanding invisible collision volumes — see `CollisionZone`. */
  collisionZones?: CollisionZone[];
  /** Ground-projected decorative texture patches — see `Decal`. */
  decals?: Decal[];
  /**
   * Procedural roads/paths — see `Road`. A separate top-level array rather
   * than `WorldObject`s because a road has no footprint, no interaction and
   * no single position; it is a polyline, like `collisionZones` and `decals`
   * are their own arrays for the same reason.
   */
  roads?: Road[];
  /**
   * Hanging power cables — see `Cable`. A separate top-level array for the
   * same reason `roads` is: a cable is a polyline, not an object with a
   * footprint.
   */
  cables?: Cable[];
  /**
   * Where a raid player can extract — see `RaidZone`. Map-editor authoring
   * only; not wired into raid logic yet (see `RaidZone`'s SCOPE note).
   */
  escapeZones?: RaidZone[];
  /**
   * Where enemy raiders can spawn — see `RaidZone`. Map-editor authoring
   * only; not wired into raid logic yet (see `RaidZone`'s SCOPE note).
   */
  raiderSpawnZones?: RaidZone[];
  /**
   * Standalone hand-placed sprite-VFX (fire/smoke) — see `SpriteSpawn`. Not
   * derived from any object's model — nothing lights itself any more.
   */
  spriteSpawns?: SpriteSpawn[];
  /**
   * Where fighters start in a PvP map — see `PlayerSpawn`.
   *
   * Separate from `spawnPoint`, which is "where the LOCAL player stands when
   * this map loads" and is singular by definition. A battleground needs one
   * per seat, they are not interchangeable with each other, and the SERVER
   * picks which fighter gets which — so this is authored content that leaves
   * the client, not a client-side default.
   */
  playerSpawns?: PlayerSpawn[];
  /**
   * Per-map camera overrides, merged over `DEFAULT_CAMERA`. Omit for the
   * shipped 17-30m orbit every playable map uses.
   *
   * Exists for `test_dummies`, which needs to get within ~1m of a character
   * to inspect a garment seam or a grip — a range `DEFAULT_CAMERA` explicitly
   * rules out ("the old 10m floor put the camera inside the settlement's
   * props"). That reasoning is about a map full of buildings and still holds
   * there; an empty inspection map has nothing to end up inside of.
   */
  camera?: Partial<CameraConfig>;
  /**
   * Hide the player's own body and name tag.
   *
   * Only sane on an inspection map, and there it is REQUIRED, not cosmetic.
   * The camera orbits the player and never free-pans (see `CameraController`),
   * so at a 1m orbit distance the camera sits inside the player's own head and
   * the entire frame is the back of their hair — which is exactly what the
   * first `test_dummies` close-up screenshot showed. With the body hidden the
   * same 1m orbit becomes a floating close-range camera you can walk up to any
   * dummy with.
   */
  hidePlayerModel?: boolean;
  /**
   * Per-map lighting override. Omit for the shipped dusk-firelight look every
   * playable map uses (warm key + cool fill + a campfire point light, over a
   * near-black background with fog from 28m).
   *
   * Exists for the same reason `camera` does: that look is a deliberate mood
   * for a settlement at dusk and is actively wrong for an inspection map,
   * where everything is in shadow and the fog eats the far rows. A rig for
   * LOOKING at art wants flat, bright, neutral light and no fog.
   */
  lighting?: {
    /** Scene background + fog colour. */
    background?: number;
    /** Hemisphere fill intensity. */
    fill?: number;
    /** Directional key intensity. */
    key?: number;
    /** Campfire point-light intensity — 0 removes the flicker entirely. */
    campfire?: number;
    /** `false` disables distance fog. */
    fog?: boolean;
    /**
     * Ground-plane colour when the map has no ground TEXTURE. The shipped
     * fallback is `0x2b2216` — near-black brown, which is right under
     * firelight and is most of why an untextured map renders almost black.
     */
    ground?: number;
    /**
     * Hemisphere light's LOWER colour. The shipped `0x140d08` is near-black,
     * so surfaces facing away from the key get almost nothing bounced back;
     * raising it is what actually makes a subject readable from every angle.
     */
    fillGround?: number;
  };
  /** Ground texture + noise-overlay configuration — see `GroundTextureConfig`. */
  groundTexture?: GroundTextureConfig;
  /**
   * Optional image-based-lighting environment map — drives `scene.environment`
   * (ambient reflections/lighting on every `MeshStandardMaterial` surface:
   * the ground, the placeholder kits, and every real GLB model), NOT
   * `scene.background` — the visible sky/backdrop stays this map's flat
   * placeholder colour + fog exactly as it is today. Omit for today's
   * behaviour unchanged (`scene.environment` stays `null`).
   *
   * `url` must be an equirectangular (2:1 aspect) panorama under
   * `public/environments/` — a plain image (`.webp`/`.jpg`/`.png`), loaded
   * with `THREE.TextureLoader` and `EquirectangularReflectionMapping`, NOT
   * an `.hdr`/`.exr` (no `RGBELoader` wired up — not worth the extra
   * dependency for this game's stylized, mostly-low-roughness materials).
   * Also not run through `THREE.PMREMGenerator` (that needs a live
   * `WebGLRenderer`, which `buildScene()` deliberately doesn't take) — the
   * raw equirect texture is sampled directly. Fine for this art style;
   * revisit if reflections band visibly on a rough surface.
   *
   * No such asset ships with the project yet (see `public/environments/` —
   * empty until someone adds one), so `ultra_map_editor`'s World panel
   * dropdown for this starts empty. See `render/scene.ts`'s
   * `SceneHandles.setEnvironmentMap`.
   */
  environment?: { url: string; intensity?: number };
  /**
   * Optional pre-baked minimap picture (e.g. `/minimaps/new-ashford.png`),
   * used as the minimap's base layer instead of deriving it from `objects` at
   * runtime. Bake one with the minimap widget's dev helper
   * (`window.__bakeMinimap()` in a DEV build), touch it up, drop it under
   * `frontend/public/`, and point this at it. Must cover the same square
   * extent the derived bake does (centred on the world origin, spanning
   * `max(ground.width, ground.depth)`) or markers will sit off-position.
   * Falls back to the derived bake if the image fails to load.
   */
  minimapImageUrl?: string;
}

export interface PlayerState {
  position: Vec2;
  /** Yaw in radians (0 = facing +Z), direction the body points. */
  facing: number;
  /** Collision radius (metres). */
  radius: number;
  /** Max ground speed (metres/second). */
  speed: number;
  moving: boolean;
}

/**
 * Per-frame input intent, camera-relative and normalised.
 * The sim rotates this into world space using the supplied camera yaw.
 */
export interface InputState {
  /** Strafe: right = +1, left = -1. */
  moveX: number;
  /** Forward (into the screen) = +1, back = -1. */
  moveZ: number;
  /**
   * World-space yaw (radians, 0 = +Z, matching `PlayerState.facing`'s own
   * convention) the character should face while aiming — see base-sim.ts's
   * aim branch. `null`/`undefined` means "not aiming": facing follows the
   * movement direction as before, and `moveX`/`moveZ` stay camera-relative.
   * Recomputed every frame by the caller (index.ts) while RMB is held,
   * including frames with zero `moveX`/`moveZ` — tracking the cursor while
   * stationary is the point of the scheme (see `design/systems/character-
   * animation-runtime.md` §3.7). All three new fields on this interface are
   * optional so every existing 3-arg `sim.update(dt, input, cameraYaw)` call
   * site (every `base-sim.*.test.ts` file, `location-view.ts`) keeps
   * compiling unchanged.
   */
  aimYaw?: number | null;
  /**
   * Persistent walk/run toggle (Capslock in index.ts) — not a held key.
   * `undefined` behaves as `'run'` (today's only speed, unchanged).
   * Deliberately NOT mirrored onto `PlayerState` in this pass: nothing
   * downstream of the sim needs to read it back yet (index.ts already owns
   * this as toggle state and feeds the HUD from that directly, not via the
   * snapshot). Revisit once a Phase 1 animation consumer wants it from the
   * same per-frame snapshot it already reads, instead of a second channel.
   */
  gait?: 'walk' | 'run';
  /**
   * Persistent crouch toggle ('c' in index.ts), independent of `gait`. No
   * sim behavior reads this yet in this pass (no speed/collision change) —
   * see `gait`'s doc comment above for why it isn't mirrored onto
   * `PlayerState` either.
   */
  crouched?: boolean;
}

export type InteractionState =
  | { kind: 'none' }
  /** `npcId`/`lootObjectId` mirror `WorldObject`'s own pair — exactly one is set. */
  | { kind: 'near'; objectId: ObjectId; npcId?: NpcId; lootObjectId?: string }
  | { kind: 'dialog'; npcId: NpcId; nodeId: string };

export interface NearestInteractable {
  objectId: ObjectId;
  npcId?: NpcId;
  lootObjectId?: string;
  distance: number;
}

/** Read-only view of sim state the renderer/UI consume each frame. */
/**
 * Live pose of one ambient wandering citizen (`WorldObject.wanderRadius`).
 * The renderer syncs the NPC's root transform from this every frame — unlike
 * every other `WorldObject`, whose transform is written once at attach time.
 * Keyed by object id, since a citizen has no `npcId`.
 */
export interface ActorPose {
  objectId: ObjectId;
  position: Vec2;
  /** Yaw in radians (0 = facing +Z), matching `PlayerState.facing`. */
  facing: number;
  /**
   * What the body should be doing right now. The renderer pushes this into
   * `NpcAnimator.setActivity()`, which no-ops when unchanged — so this can be
   * read every frame without churning the mixer.
   *
   * A `wanderRadius` citizen is always `'walk'`; a routine NPC alternates
   * between `'walk'` while travelling and its current step's own activity
   * while dwelling.
   */
  activity: NpcActivity;
  /** True while the body should not be drawn or offered as an interactable — see `RoutineStep.hidden`. */
  hidden: boolean;
}

export interface SimSnapshot {
  player: PlayerState;
  interaction: InteractionState;
  nearestInteractable: NearestInteractable | null;
  /**
   * Live poses for every self-moving NPC body — ambient `wanderRadius`
   * citizens and scripted `routine` NPCs alike. Empty unless the map places
   * some. Named `actors` rather than `wanderers` since routines landed: these
   * are no longer all aimless drifters.
   */
  actors: readonly ActorPose[];
}

/**
 * The simulation contract. Pure TypeScript — no Three.js, no DOM.
 * The render loop calls `update()` every frame; UI calls the dialog methods.
 */
export interface BaseSim {
  readonly world: WorldData;
  getSnapshot(): SimSnapshot;
  /**
   * Advance the sim by `dt` seconds. `cameraYaw` maps camera-relative input
   * into world space so "forward" is always away from the camera.
   */
  update(dt: number, input: InputState, cameraYaw: number): void;
  /**
   * Pushes the live, character-stat-derived speed multipliers into the sim —
   * called after equipment loads (async) and again on every subsequent
   * change (gear swap), not just once at construction (character-animation-
   * runtime.md §3.4/§3.7 follow-up). Both args are GDD-space multipliers in
   * the same "1.0 = base, no bonus" unit system as `character-stats.md` F2's
   * `runSpeed`/the new `crouchSpeed` formula — NOT world-space metres/second
   * (see `base-sim.ts`'s `DEFAULT_RUN_SPEED` doc comment for the mapping
   * between the two). Deliberately narrow (two named numbers, not a partial
   * `PlayerState` patch) — a new setter, not a change to `update()`'s
   * parameter list.
   * @param runSpeedMultiplier `equipment.blendedStats.runSpeed` as-is.
   * @param crouchSpeedMultiplier `crouchSpeedMultiplier(agility, perception)` from `base-sim.ts`.
   */
  setStatSpeeds(runSpeedMultiplier: number, crouchSpeedMultiplier: number): void;
  /**
   * Put the player somewhere, immediately — no walk, no collision sweep.
   *
   * For the case where a SERVER owns the position and the local sim has
   * drifted from it. The duel is the one that exists: `BattlegroundRoom`
   * seats both fighters on their authored spawn points and refuses every
   * `move` message while the pre-duel freeze is running, but the client's own
   * sim kept walking — so a player who wandered the arena waiting for an
   * opponent was, to the room and to the other player, still standing on the
   * spawn they had left. Janeks: *"When opponent is connected we should be
   * frozen and Teleported to the spawn zone!"*
   *
   * Velocity is cleared with the position. Without that the sim keeps the
   * momentum it had at the old place and slides away from the new one, which
   * would undo the teleport over the following frames.
   */
  setPlayerPosition(position: Vec2): void;
  /** Start talking to the nearest interactable if in range; else null. */
  tryInteract(): DialogNode | null;
  /** Pick a dialog option; returns the next node, or null if it ended. */
  chooseOption(optionIndex: number): DialogNode | null;
  /** The active dialog node, or null when not in dialog. */
  currentDialog(): DialogNode | null;
  endDialog(): void;
  /**
   * Turn an authored-`interactable` object inert, or back on.
   *
   * For the case where "can this be used" is a LIVE fact the server owns
   * rather than a property of the map — the player's house, which is a
   * boarded-up building until the deed is granted (story quest or the Gold
   * home key) and their front door afterwards.
   *
   * Suppression is applied inside `recomputeNearest`, the sim's single answer
   * to "what is in reach", so every reader goes quiet together: the E key, the
   * click-to-interact path, the "Press E" prompt, the pointer cursor and the
   * hover glow. The alternative — a guard at each of those five call sites —
   * is exactly the drift `ui/panels.ts` exists to prevent.
   *
   * `WorldData` is never written: map modules are shared singletons imported
   * by both the game and the editor.
   */
  setInteractableSuppressed(objectId: ObjectId, suppressed: boolean): void;
}

/** Constrained-camera configuration (see camera-controller). */
export interface CameraConfig {
  /** Fixed elevation from the horizon (radians) — the "almost-isometric" tilt. */
  pitch: number;
  /** Orbit distance at each zoom extreme (metres). Zoom only; no free pan. */
  minDistance: number;
  maxDistance: number;
  defaultDistance: number;
  /** Radians of yaw per pixel of horizontal drag. */
  rotateSpeed: number;
  /** Metres of distance per wheel notch. */
  zoomSpeed: number;
  /**
   * Optional floor on how much ground the frame shows around the player,
   * metres, in EVERY direction — see `orbitForGroundRadius` in
   * `render/camera-controller.ts`. Unset (the settlement) flies the zoom level
   * as is, so a portrait phone sees a few metres to each side.
   */
  minGroundRadius?: number;
}

export const DEFAULT_CAMERA: CameraConfig = {
  pitch: (55 * Math.PI) / 180,
  // Janeks, after reading the live zoom readout in the debug overlay:
  // *"Set zoom min-max to 17-30m"*. The old 10 m floor put the camera inside
  // the settlement's props; 17 m keeps the player's surroundings in frame at
  // every zoom level.
  minDistance: 17,
  maxDistance: 30,
  defaultDistance: 20,
  rotateSpeed: 0.005,
  zoomSpeed: 1.4,
};

/**
 * How far every duellist is guaranteed to see around themselves, metres, on any
 * screen — and therefore how far a bot may see (`BOT_SIGHT_RANGE_M` in
 * `backend/src/game/battleground/bot-brain.ts`, which must equal this).
 *
 * Janeks, 2026-10-16: *"Bot shot me down … i did not see him, zoomed out
 * completely"* and *"They see their enemies only at max viewport distance"*.
 * The bot had a 35 m sight range; the frame at full zoom-out shows 12.6 m
 * BEHIND the player (the camera looks down at 55 deg, so the bottom edge is
 * always the near one) and 7 m to the side on a portrait phone. 12 m is the
 * behind edge at the arena's pinned orbit, rounded down, and
 * `orbitForGroundRadius` pulls a narrow screen's camera back until its sides
 * reach it.
 */
export const ARENA_SIGHT_RADIUS_M = 12;

/**
 * The duel camera: pinned at the settlement's widest zoom (*"set the viewport
 * max zoom, so that on all screens everybody has the same fov"*) and fitted to
 * `ARENA_SIGHT_RADIUS_M`. Overrides `DEFAULT_CAMERA` and any map's own
 * `camera` — a duel map that tuned its zoom would break the fairness promise.
 */
export const ARENA_CAMERA: Partial<CameraConfig> = {
  minDistance: DEFAULT_CAMERA.maxDistance,
  maxDistance: DEFAULT_CAMERA.maxDistance,
  defaultDistance: DEFAULT_CAMERA.maxDistance,
  minGroundRadius: ARENA_SIGHT_RADIUS_M,
};
